import { useMemo } from "react"

import cls from "classnames"

import { getPhoneMaxLength } from "../../Utils/onboarding/getPhoneMaxLength"
import { sanitizePhoneNumber } from "../../Utils/onboarding/sanitizePhoneNumber"
import { formatPhoneNumber } from "../../Utils/phone/formatPhoneNumber"
import { getCachedCountries } from "../../Utils/phoneCountriesCache"
import InputComponent from "../Input"
import CountrySelect from "./components/CountrySelect"
import { getPhoneAfterBackspace, isBackspaceOnFormattingChar } from "./utils/helpers"

import "./styles.less"

const PhoneInput = ({
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder = "Enter phone number",
  disabled,
  ...props
}) => {
  const code = value?.code || ""
  const country_name = value?.name || ""
  const number = value?.number || ""
  const countryCode = value?.countryCode
  const countries = getCachedCountries() || []
  const maxLength = getPhoneMaxLength(countryCode)

  const displayNumber = useMemo(
    () => formatPhoneNumber(code, `${number}`, countryCode),
    [number, countryCode]
  )

  const triggerChange = (changedValue) => {
    onChange?.({
      code,
      number,
      ...changedValue
    })
  }

  const handlePhoneChange = (e) => {
    triggerChange({ ...value, number: sanitizePhoneNumber(e.target.value, maxLength) })
  }

  const handleCountryChange = (selectedCountry) => {
    const country = countries.find((c) => c.name === selectedCountry)
    if (!country) return

    triggerChange({
      code: country.code,
      countryCode: country.country_code,
      name: country.name,
      id: country.id,
      number: sanitizePhoneNumber(`${number}`, getPhoneMaxLength(country.country_code))
    })
  }

  const handlePhoneKeyDown = (e) => {
    if (e.key !== "Backspace") return
    if (!isBackspaceOnFormattingChar(e)) return

    e.preventDefault()

    triggerChange({
      ...value,
      ...getPhoneAfterBackspace({
        inputValue: e.currentTarget.value,
        cursorPosition: e.currentTarget.selectionStart,
        maxLength,
        code,
        countryCode
      })
    })
  }

  return (
    <InputComponent
      name="phone"
      inputMode="numeric"
      className={cls("phoneInput")}
      disabled={disabled}
      addonBefore={
        <CountrySelect
          countries={countries}
          value={country_name}
          disabled={disabled}
          onChange={handleCountryChange}
        />
      }
      value={displayNumber}
      onChange={handlePhoneChange}
      onKeyDown={handlePhoneKeyDown}
      onFocus={onFocus}
      placeholder={placeholder}
      onBlur={onBlur}
      {...props}
    />
  )
}

export default PhoneInput
